<script setup lang="ts">
  import { RIGA_TIMEZONE, SB_DATE_FORMAT } from '@/utils/constants/date'
  import { getRemainingRigaTzTime } from '@/utils/dayjs/utils'
  import type { ICampaignDates } from '@/components/campaign/banner/types'
  import { cva } from 'class-variance-authority'
  import type { TCounterVariant, TCounterTextColor } from '@/components/campaign/counter/types'

  const props = withDefaults(
    defineProps<{
      startDate?: string
      endDate?: string
      hasEnded?: boolean
      variant?: TCounterVariant
      textColor?: TCounterTextColor
    }>(),
    {
      endDate: '',
      startDate: '',
      hasEnded: false,
      variant: 'default',
      textColor: 'default'
    }
  )

  const dayjs = useDayjs()

  const campaignDates = inject<ICampaignDates>('campaignDates')

  const remainingDays = ref(0)
  const remainingHours = ref(0)
  const remainingMinutes = ref(0)
  const remainingSeconds = ref(0)

  const ariaRemainingDays = ref(0)
  const ariaRemainingHours = ref(0)
  const ariaRemainingMinutes = ref(0)
  const ariaRemainingSeconds = ref(0)

  // SB date comes without seconds
  const currentTime = ref(
    campaignDates?.currentTime ? dayjs(campaignDates?.currentTime).tz(RIGA_TIMEZONE) : dayjs.utc().tz(RIGA_TIMEZONE)
  )

  const startDate = computed(() => {
    if (!props.startDate) return null
    const date = dayjs.tz(props.startDate, SB_DATE_FORMAT, RIGA_TIMEZONE)

    return date.isValid() ? date.add(59, 'seconds') : null
  })

  const endDate = computed(() => {
    if (!props.endDate) return null
    const date = dayjs.tz(props.endDate, SB_DATE_FORMAT, RIGA_TIMEZONE)

    return date.isValid() ? date.add(59, 'seconds') : null
  })

  const campaignStarted = ref(currentTime.value.isAfter(startDate.value, 'seconds'))

  function setRemainingTime() {
    if (!startDate.value || !endDate.value) return
    const duration = getRemainingRigaTzTime(currentTime.value, campaignStarted.value ? endDate.value : startDate.value)

    remainingDays.value = duration.days()
    remainingHours.value = duration.hours()
    remainingMinutes.value = duration.minutes()
    remainingSeconds.value = duration.seconds()

    // When campaign countdown ends, wrapper isCampaignActive is updated to false to switch view
    if (currentTime.value.isAfter(endDate.value, 'seconds')) {
      clearInterval(counterInterval.value)
      campaignDates?.updatedIsCampaignActive?.(false)

      return
    } else if (
      currentTime.value.isAfter(startDate.value, 'seconds') &&
      currentTime.value.isBefore(endDate.value, 'seconds')
    ) {
      campaignStarted.value = true
    }

    currentTime.value = currentTime.value.add(1, 'second')
  }

  const counterInterval = ref<NodeJS.Timeout>()

  onMounted(() => {
    if (props.hasEnded || !endDate.value) {
      return
    }

    counterInterval.value = setInterval(setRemainingTime, 1000)
  })

  onBeforeUnmount(() => {
    if (counterInterval.value) {
      clearInterval(counterInterval.value)
    }
  })

  watch(remainingMinutes, () => {
    ariaRemainingDays.value = remainingDays.value
    ariaRemainingHours.value = remainingHours.value
    ariaRemainingMinutes.value = remainingMinutes.value
    ariaRemainingSeconds.value = remainingSeconds.value
  })

  const getFormattedRemainingTime = (time: number) => time.toString().padStart(2, '0')

  const formattedRemainingDays = computed(() => getFormattedRemainingTime(remainingDays.value))
  const formattedRemainingHours = computed(() => getFormattedRemainingTime(remainingHours.value))
  const formattedRemainingMinutes = computed(() => getFormattedRemainingTime(remainingMinutes.value))

  const counterBase = computed(() =>
    cva('flex w-fit items-center gap-2 sm:gap-4 lg:gap-6', {
      variants: {
        variant: {
          default: 'counter-shadow bg-white rounded-full px-10 py-2 lg:px-14',
          plain: 'p-0 bg-transparent'
        },
        textColor: {
          default: 'text-bt-blue-dark',
          white: 'text-white'
        }
      }
    })({
      variant: props.variant,
      textColor: props.textColor
    })
  )
</script>
<template>
  <div class="flex flex-col items-center gap-4">
    <p
      v-if="!hasEnded && variant !== 'plain'"
      class="m-0 text-subtitle-1"
    >
      {{ !campaignStarted ? $t('counter.startsIn') : $t('counter.endsIn') }}
    </p>
    <div
      :class="counterBase"
      :aria-label="
        $t('date.remainingTime', {
          days: ariaRemainingDays,
          hours: ariaRemainingHours,
          minutes: ariaRemainingMinutes,
          seconds: ariaRemainingSeconds
        })
      "
    >
      <CampaignCounterDigits
        :time="formattedRemainingDays"
        :unit="$t('date.days', remainingDays)"
      />
      <span class="text-h2"> : </span>
      <CampaignCounterDigits
        :time="formattedRemainingHours"
        :unit="$t('date.hours', remainingHours)"
      />
      <span class="text-h2"> : </span>
      <CampaignCounterDigits
        :time="formattedRemainingMinutes"
        :unit="$t('date.minutes', remainingMinutes)"
      />
      <span class="text-h2"> : </span>
      <CampaignCounterDigits
        :time="getFormattedRemainingTime(remainingSeconds)"
        :unit="$t('date.seconds', remainingSeconds)"
      />
    </div>
  </div>
</template>
